<template>
  <div v-if="loading" class="loading flex items-center justify-center bg-producer-background/60">
    <div class="h-8 w-8 animate-spin rounded-full border-2 border-producer-primary border-t-transparent" />
  </div>

  <div v-else-if="!isAuthorized" class="error-message flex h-full items-center justify-center gap-3 bg-producer-background px-6 text-producer-text">
    <h1 class="text-2xl font-semibold">
      Something went wrong.
    </h1>

    <button
      class="rounded-md border border-producer-border px-4 py-2 text-sm font-medium text-producer-text transition hover:bg-producer-surfaceAlt"
      @click="reload"
    >
      Reload Page
    </button>
  </div>

  <div v-else class="app bg-producer-background text-producer-text" data-test="producer-app">
    <nav-bar
      :icon="icon"
      :nav-links="navLinks"
      :active-tab="activeTab"
      :launcher-applications="launcherApplications"
      :launcher-discover-applications="launcherDiscoverApplications"
      :launcher-staff-tools="launcherStaffTools"
      data-test="producer-tgbNavBar"
      :classes="classes"
      :user="user"
      :defaultAvatar="defaultAvatar"
      @click-navbar-link="() => {}"
      @click-navbar-icon="handleClickNavbarIcon"
      @click-launcher-application="handleClickLauncherApplication"
    >
      <template #title>
        <router-link
          to="/"
          class="home-link"
          data-test="producer-navLink"
        >
          <h1 class="navbar-title">Tagboard <span>producer</span></h1>
        </router-link>
      </template>

      <!-- A little jank,
        so adding extra failsafes to make sure this doesn't render outside a production -->
      <template #default>
        <nav-component
          v-if="production && production._id && currentRoute !== 'productions'"
        />
      </template>
    </nav-bar>

    <router-view />

    <version-check-toast />

    <warning-toast
      v-if="!isSocketConnected"
      title="Lost Connection"
      description="We're trying to reconnect your Production. If nothing happens, try refreshing the page."
    />

    <toast />
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { Userpilot } from 'userpilot';

import NavBar from './navbar/NavBar.vue';
import NavComponent from './nav/Nav.vue';
import VersionCheckToast from './VersionCheckToast.vue';
import WarningToast from './WarningToast.vue';
import Toast from './Toast.vue';
import ConduitEventsService from '../services/conduitEventsService';

export default {
  components: {
    NavBar,
    NavComponent,
    VersionCheckToast,
    WarningToast,
    Toast,
  },

  watch: {
    userId(userId) {
      if (userId && !this.identified) {
        // Identify for analytics and tracking
        this.identify();
      }
    },

    $route(to) {
      Userpilot.reload();
      this.currentRoute = to.name;

      if (this.currentRoute === 'productions') {
        this.$store.dispatch('clearProduction');
        document.querySelector('title').innerText = 'Tagboard Producer';
      }
    },
  },

  data() {
    return {
      loading: true,
      isAuthorized: true,
      identified: false,
      currentRoute: this.$route.name,
      listener: null,
      icon: './producer/assets/img/logo-producer-new.svg',
      classes: 'dark navbar',
      activeTab: '',
      navLinks: [],
      launcherApplications: [
        {
          name: 'Producer',
          url: process.env.TAGBOARD_PRODUCER_URL,
          icon: './producer/assets/img/logo-producer-new.svg',
          dataTest: 'producer-megaMenu-producer',
        },
        {
          name: 'Creator',
          url: process.env.TAGBOARD_ACCOUNT_URL,
          icon: './producer/assets/img/logo-creator-new.svg',
          dataTest: 'producer-megaMenu-creator',
        },
      ],
      launcherDiscoverApplications: [
        {
          name: 'Support',
          url: 'https://support.tagboard.com/knowledge-base',
          icon: './producer/assets/img/logo-support-new.png',
          dataTest: 'producer-megaMenu-support',
        },
        {
          name: 'Product Updates',
          url: `https://${process.env.DOMAIN}/category/product/`,
          icon: './producer/assets/img/logo-product-updates-new.png',
          dataTest: 'producer-megaMenu-product-updates',
        },
      ],
      launcherStaffTools: [
        {
          name: 'Tashboard',
          url: process.env.TAGBOARD_TASHBOARD_URL,
          icon: './producer/assets/img/logo-tashboard.svg',
          dataTest: 'producer-megaMenu-tashboard',
        },
      ],
      defaultAvatar: './producer/assets/img/profile_placeholder.png',
      domain: process.env.DOMAIN,
    };
  },

  async created() {
    await this.getUser();
    await ConduitEventsService.init(this.userId);
  },

  mounted() {
    this.listener = window.addEventListener('keyup', (event) => {
      // Ignore key presses if user isn't inside a production
      if (!this.production?._id) {
        return;
      }

      // Ignore if inside an `<input>` element
      if (event.target?.nodeName === 'INPUT') { return; }

      let direction;
      if (event.keyCode === 39) {
        direction = 'next';
      } else if (event.keyCode === 37) {
        direction = 'previous';
      } else if (event.keyCode === 32) {
        this.$store.dispatch('togglePostVideo');
      }

      if (direction) {
        this.advance(direction);
      }
    });
  },

  unmounted() {
    window.removeEventListener('keyup', this.listener);
  },

  methods: {
    reload() {
      window.location.reload();
    },

    identify() {
      // Initialize Userpilot
      Userpilot.initialize(process.env.USERPILOT_TOKEN);
      if (!this.user.groups?.includes('staff')
        && !process.env.NODE_ENV === 'stage'
        && !process.env.NODE_ENV === 'development'
      ) { // exclude staff and non-prod environments
        Userpilot.identify(this.userId, {
          email: this.user.email,
          name: this.user.displayName,
        });
      }

      this.identified = true;
    },

    async getUser() {
      try {
        this.isAuthorized = await this.$store.dispatch('getUserInfo');
      } catch (err) {
        console.error('Failed to get info for logged in user:', err);
        this.isAuthorized = false;
      } finally {
        this.loading = false;
      }
    },

    advance(direction) {
      this.$store.dispatch('advance', direction);
    },

    handleClickNavbarIcon(icon) {
      switch (icon.id) {
        case 'bellIcon':
          this.$store.dispatch('toggleHighlightsModal');
          break;

        default: break;
      }
    },

    handleClickLauncherApplication(application) {
      window.open(application.url, '_blank');
    },
  },

  computed: {
    ...mapState({
      userId: (state) => state.user.id,
      production: (state) => state.production.production,
      isSocketConnected: (state) => state.clients.isSocketConnected,

      // Gotta add a couple attributes for the navbar component
      user: (state) => ({
        ...state.user,
        user_id: state.user.id,
        avatar: state.user.profileImageUrl,
      }),
    }),

    /**
    * Using production ID as router key forces a reload if user pastes in a new production URL,
    * or uses the back button to navigate
    */
    routerKey() {
      return this.$route.params?.production_id || null;
    },
  },
};
</script>

<style lang="scss" scoped>
.error-message {
  margin-top: 2rem;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
}

.navbar-title {
  text-transform: none;
  letter-spacing: 0;
  margin: 0;
  font-weight: 600;

  span {
    font-weight: 400;
  }
}

.home-link {
  text-decoration: none;
  color: inherit;

  &:hover,
  &:focus,
  &:active {
    text-decoration: none;
    color: inherit;
  }
}
</style>
